import $ from 'jquery';
import Flickity from 'flickity';
import debounce from 'just-debounce';
import VanillaModal from 'vanilla-modal';
import Vimeo from '@vimeo/player/dist/player';
import yt from '../globals/youtube';

export default class HomeSlideshow {
  constructor(section) {
    this.$body = $(document.body);
    this.$window = $(window);
    this.$section = section;
    this.$el = $(section.el);
    this.data = section.data;
    this.flickity = null;
    this.isInteractive = this.data.slide_count > 1;

    this.$slideshow = this.$el.find('[data-slideshow]');
    this.$slides = this.$el.find('[data-slideshow-slide]');
    this.autoplay = this.data.should_autoplay && this.data.autoplay
      ? this.data.autoplay * 1000
      : false;
    this.pauseDelay = this.autoplay ? 5000 : 0;
    this.timer = null;

    this.windowWidth = this.$window.width();
    this.gridSpacing = parseInt(window.Theme.gridSpacing, 10) || 0;
    this.hasSidebar = window.Theme.hasSidebar;
    this.fadeOpacity = false;
    this.$slideNavigation = this.$el.find('.dot');
    this.continueAutoplay = true;
    this.autoplayTimeout = 0;

    this.$videoSlides = this.$el.find('.slideshow-slide-video-background');

    this.modalId = `slideshow-video-${this.$section.id}-modal`;
    this.$modal = this.$el.find(`.${this.modalId}-content`);
    this.$modalToggleOpen = this.$el.find('[data-slideshow-video-modal-open]');
    this.$modalToggleClose = this.$el.find('[data-slideshow-video-modal-close]');


    if (this.isInteractive) {
      this._initFlickity();
    }

    if (this.$videoSlides.length) {
      if (this.$el.find('.overlay-opacity-100').length === 0 && this.$el.find('[data-slideshow-slide]:nth-of-type(1) .slideshow-slide-video-background').length > 0) {
        this.fadeOpacity = true;
        this.$el.find('.slideshow-slide-info-container').addClass('overlay-opacity-100');
      }
    }

    this._initVideoSlides();
    this._bindEvents();
  }

  _initFlickity() {
    this.flickity = new Flickity(this.$slideshow[0], {
      accessibility: true,
      adaptiveHeight: true,
      autoPlay: this.autoplay,
      cellSelector: '[data-slideshow-slide]',
      pageDots: true,
      pauseAutoPlayOnHover: false,
      prevNextButtons: false,
      wrapAround: true,
      selectedAttraction: 0.01,
      friction: 0.15,
    });

    this._bindSlideshow();
  }

  _bindSlideshow() {
    if (this.data.slide_count > 1 && this.autoplay) {
      this._togglePaused(true);

      this.flickity.on('cellSelect', () => {
        if (this.continueAutoplay) {
          this.autoplayTimeout = 0;
        } else {
          this.autoplayTimeout = 10000;
        }

        this.continueAutoplay = true;
        this._togglePaused(false);

        this._setAutoplayTimer();
      });

      this.flickity.on('dragStart', () => {
        this.continueAutoplay = false;
      });

      this.flickity.on('staticClick', () => {
        if (this.autoplay && this.isInteractive) {
          this.flickity.pausePlayer();
          this._togglePaused(true);

          clearTimeout(this.timer);

          this.timer = setTimeout(() => {
            this._setAutoplayTimer();
          }, this.pauseDelay);
        }
      });
    }

    this.$slides.on('focusin.slideshow', (event) => {
      event.preventDefault();
      event.stopPropagation();

      const slideIndex = this.$slides.index($(event.currentTarget));
      this.flickity.select(slideIndex, false, true);
      this.flickity.reloadCells();
    });
  }

  _initVideoSlides() {
    this.$videoSlides.each((index, el) => {
      const parentSlide = $(el).parents('.slideshow-slide');
      const slideId = $(el).attr('id');
      const videoId = $(el).attr('data-video-id');
      const videoType = $(el).attr('data-video-type');
      if (!videoId) return;

      // Remove YouTube API iframes from the slides if mobile device
      if (/Mobi/i.test(navigator.userAgent) || /Android/i.test(navigator.userAgent)) {
        this._fadeOpacity();

        return;
      }

      if (videoType === 'vimeo') {
        this._initVimeo(slideId, videoId, parentSlide);
      } else {
        this._initYoutube(slideId, videoId, parentSlide);
      }
    });
  }

  _initYoutube(slideId, videoId, parentSlide) {
    yt(() => {
      const player = new YT.Player(slideId, {
        videoId: videoId,
        events: {
          onReady: (mute) => {
            player.mute();

            if (parentSlide.hasClass('is-selected') || !this.flickity) {
              player.playVideo();
            } else {
              player.pauseVideo();
            }

            if (this.flickity) {
              this.flickity.on('cellSelect', () => {
                if (parentSlide.hasClass('is-selected')) {
                  player.playVideo();
                } else {
                  player.pauseVideo();
                }
              });
            }

            this.$modalToggleOpen.on('click.slideshow-modal', (event) => {
              player.pauseVideo();
            });

            this.$modalToggleClose.on('click.slideshow-modal', (event) => {
              player.playVideo();
            });

            this._fadeOpacity();
          },
        },
        playerVars: {
          enablejsapi: 1,
          autoplay: 1,
          controls: 0,
          showinfo: 0,
          modestbranding: 1,
          playlist: videoId,
          loop: 1,
          autohide: 0,
          rel: 0,
        }
      });
    });
  }

  _initVimeo(slideId, videoId, parentSlide) {
    const options = {
      id: videoId,
      width: 640,
      loop: true
    };

    const player = new Vimeo(slideId, options);
    player.setVolume(0);

    this._fadeOpacity();

    if (parentSlide.hasClass('is-selected') || !this.flickity) {
      player.play();
    } else {
      player.pause();
    }

    if (this.flickity) {
      this.flickity.on('cellSelect', () => {
        if (parentSlide.hasClass('is-selected')) {
          player.play();
        } else {
          player.pause();
        }
      });
    }

    this.$modalToggleOpen.on('click.slideshow-modal', (event) => {
      player.pause();
    });

    this.$modalToggleClose.on('click.slideshow-modal', (event) => {
      player.play();
    });
  }

  _togglePaused(paused = true) {
    if (paused) {
      $('.is-selected', this.$el).addClass('is-paused');
    } else {
      $('.is-paused', this.$el).removeClass('is-paused');
    }
  }

  _setAutoplayTimer() {
    this.timer = setTimeout(() => {
      this.flickity.playPlayer();
      this._togglePaused(true);
    }, this.autoplayTimeout);
  }

  _fadeOpacity() {
    if (this.fadeOpacity) {
      setTimeout(() => {
        $('.slideshow-slide-info-container', this.$el).removeClass('overlay-opacity-100');
      }, 700);
    }
  }

  _bindEvents() {
    this.$modalToggleOpen.on('click', event => {
      event.preventDefault();

      const $btn = $(event.currentTarget);
      const id = $btn.attr('href');
      this._openModal(id);
    });

    this.$modalToggleClose.on('click', event => {
      event.preventDefault();
      this._closeModal();
    });

  }

  _centerModal($modal) {
    const modalHeight = $modal.outerHeight();
    const headerHeight = !this.hasSidebar && $(this.$section.el).is(':first-child') ? 80 : 0;
    const marginTop = (window.innerHeight - modalHeight - headerHeight - (this.gridSpacing * 2)) / 2;

    $modal.css({marginTop});

    $('html, body').animate({
      scrollTop: this.$el.offset().top
    }, 1000);
  }

  _openModal(id) {
    const $modal = this.$el.find(`.${this.modalId}-content`);

    this.modal = new VanillaModal({
      loadClass: 'modal-loaded',
      class: '',
      modal: `.${this.modalId}`,
      modalInner: `.${this.modalId}-inner`,
      modalContent: `.${this.modalId}-content`,
      onOpen: () => {
        this.$body
          .addClass('overlay-visible')
          .addClass(`${this.modalId}-visible`);

        this._centerModal($modal);

        this.$window.on('resize.slideshow-modal', debounce(() => {
          if (this.windowWidth !== this.$window.width()) {
            this._centerModal($modal);
          }
        }, 200));

        this._togglePaused(false);
        this.flickity.pausePlayer();

        this.$el.find('.slideshow-slide-video-icon').toggleClass('hide');

        // Autoplay
        const iframe = this.$modal.find('iframe')[0];
        if (iframe) {
          iframe.src += '&autoplay=1';

          if (iframe.src.match(/youtube/)) {
            const player = new YT.Player(iframe.id, {
              events: { onReady: event => player.playVideo() },
            });
          }
        }
      },
    });

    this.modal.open(id);
  }

  _closeModal() {

    this.$body
      .removeClass('overlay-visible')
      .removeClass(`${this.modalId}-visible`);

    this.$el.find('.slideshow-slide-video-icon').toggleClass('hide');

    this.$window.off('.slideshow-modal');

    const iframe = this.$modal.find('iframe')[0];
    if (iframe) {
      iframe.src = iframe.src.replace('&autoplay=1', '');
    }

    if (this.modal) {
      this._togglePaused(true);
      this.flickity.playPlayer();
      this.$window.off('.slideshow-modal');
      this.modal.close();
      this.modal.destroy();
      this.modal = null;
    }
  }

  onSectionBlockSelect(block) {
    const $slides = $('[data-slideshow-slide]');
    const $slide = $(block.el);
    const slideIndex = $slides.index($slide);
    this.continueAutoplay = false;
    if (!$slide.hasClass('is-selected') && this.flickity) {
      this.flickity.select(slideIndex, false, true);
    }

    if (this.autoplay && this.isInteractive) {
      this._togglePaused(true);
      this.flickity.pausePlayer();
    }
  }

  onSectionBlockDeselect() {
    if (this.autoplay && this.isInteractive) {
      this._togglePaused(false);
      this.flickity.unpausePlayer();
    }
  }

  onSectionUnload() {
    this.$slides.off('focusin.slideshow');
    this.$window.off('.slideshow');
    this.$window.off('.slideshow-modal');
    this.$modalToggleOpen.off('click.slideshow-modal');

    if (this.flickity) {
      this.flickity.destroy();
    }

    if (this.modal) {
      this.modal.destroy();
    }
  }
}
